/**
 * The Capital Asset Pricing Model and beta from returns.
 *
 *   expected return = Rf + β × (Rm − Rf)
 *
 * Rm − Rf is the market risk premium and β × (Rm − Rf) the asset's risk premium. Beta is the
 * slope of the regression of the asset's excess returns on the market's excess returns:
 * β = Cov(Ra, Rm) ÷ Var(Rm) (the sample n − 1 terms cancel), with the intercept alpha
 * (Jensen's alpha when the returns are excess returns). Rates are decimals unless noted;
 * the regression functions work in whatever unit the returns are given in.
 */

import { covariance, mean, pearson, variance } from "@/lib/stats/descriptive";
import { studentTQuantile } from "@/lib/stats/distributions";

export interface CapmResult {
  expectedReturn: number;
  /** Rm − Rf. */
  marketPremium: number;
  /** β × (Rm − Rf). */
  assetPremium: number;
}

export function capm(rf: number, beta: number, rm: number): CapmResult {
  const marketPremium = rm - rf;
  const assetPremium = beta * marketPremium;
  return { expectedReturn: rf + assetPremium, marketPremium, assetPremium };
}

export interface SmlPoint {
  beta: number;
  expectedReturn: number;
}

/** The security market line at `steps + 1` betas from `from` to `to`, plus any extra betas. */
export function smlPoints(
  rf: number,
  rm: number,
  from: number,
  to: number,
  steps: number,
  extra: readonly number[] = []
): SmlPoint[] {
  const betas = new Set<number>(extra);
  for (let i = 0; i <= steps; i += 1) {
    betas.add(Math.round((from + ((to - from) * i) / steps) * 1e6) / 1e6);
  }
  return [...betas]
    .sort((a, b) => a - b)
    .map((beta) => ({
      beta,
      expectedReturn: capm(rf, beta, rm).expectedReturn,
    }));
}

export interface BetaRegression {
  n: number;
  beta: number;
  alpha: number;
  correlation: number;
  rSquared: number;
  /** Null when n < 3. */
  standardError: number | null;
  /** 95% confidence interval for beta; null when n < 3. */
  interval: [number, number] | null;
  /** Blume adjustment: ⅔ β + ⅓. */
  adjustedBeta: number;
  meanAsset: number;
  meanMarket: number;
  varianceMarket: number;
  covariance: number;
}

export type BetaError = "tooFew" | "flatMarket" | "lengthMismatch";

/**
 * Regress the asset's returns on the market's, after subtracting `riskFreePerPeriod`
 * (same unit as the returns) from both. Pass 0 for a beta on raw returns.
 */
export function regressBeta(
  asset: readonly number[],
  market: readonly number[],
  riskFreePerPeriod = 0
): BetaRegression | { error: BetaError } {
  if (asset.length !== market.length) {
    return { error: "lengthMismatch" };
  }
  const n = asset.length;
  if (n < 2) {
    return { error: "tooFew" };
  }
  const y = asset.map((v) => v - riskFreePerPeriod);
  const x = market.map((v) => v - riskFreePerPeriod);
  const varianceMarket = variance(x);
  if (!(varianceMarket > 0)) {
    return { error: "flatMarket" };
  }
  const cov = covariance(x, y);
  const beta = cov / varianceMarket;
  const meanAsset = mean(y);
  const meanMarket = mean(x);
  const alpha = meanAsset - beta * meanMarket;
  const correlation = pearson(x, y);
  let sse = 0;
  for (let i = 0; i < n; i += 1) {
    sse += (y[i] - alpha - beta * x[i]) ** 2;
  }
  const sxx = varianceMarket * (n - 1);
  const standardError = n >= 3 ? Math.sqrt(sse / (n - 2) / sxx) : null;
  let interval: [number, number] | null = null;
  if (standardError !== null) {
    const half = studentTQuantile(0.975, n - 2) * standardError;
    interval = [beta - half, beta + half];
  }
  return {
    n,
    beta,
    alpha,
    correlation: Number.isNaN(correlation) ? 0 : correlation,
    rSquared: Number.isNaN(correlation) ? 0 : correlation ** 2,
    standardError,
    interval,
    adjustedBeta: (2 / 3) * beta + 1 / 3,
    meanAsset,
    meanMarket,
    varianceMarket,
    covariance: cov,
  };
}

export interface ReturnPairs {
  asset: number[];
  market: number[];
  /** The first line that is not a pair of numbers, or null. */
  invalid: string | null;
}

const TOKEN_SPLIT = /[\t;]+|\s+/;
const LINE_SPLIT = /\r?\n/;

/** Parses lines of "asset market" pairs; blank lines are skipped. */
export function parsePairs(
  raw: string,
  parse: (text: string) => number | null
): ReturnPairs {
  const asset: number[] = [];
  const market: number[] = [];
  for (const line of raw.split(LINE_SPLIT)) {
    const text = line.trim();
    if (text === "") {
      continue;
    }
    let tokens = text.split(TOKEN_SPLIT).filter((token) => token !== "");
    if (tokens.length === 1 && tokens[0].includes(",")) {
      tokens = tokens[0].split(",");
    }
    const a = tokens.length === 2 ? parse(tokens[0]) : null;
    const m = tokens.length === 2 ? parse(tokens[1]) : null;
    if (a === null || m === null) {
      return { asset, market, invalid: text };
    }
    asset.push(a);
    market.push(m);
  }
  return { asset, market, invalid: null };
}
